import React from 'react';
import { Globe, UserRound, CalendarCheck, ClipboardList, PenLine, FileText, ListChecks, Download, CheckCircle2, Link2, HeartHandshake } from 'lucide-react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { MockOpen, MockResident, MockRep, MockConfirm, MockAdmission, MockPdpa, MockChecklist, MockDone } from './CareGuideMocks';

const STEPS = [
  { icon: Globe, t: 'Open the registration page', d: 'Open the registration link your care home sent you, on your phone or computer.', Mock: MockOpen,
    pts: ['Check the care home name at the top.', 'You are on the Resident Registration page.'] },
  { icon: UserRound, t: "Fill in the resident's details", d: 'Enter the personal details of the person who will stay at the care home.', Mock: MockResident,
    pts: ["Resident's full name and NRIC/Passport no. (required).", 'Date of birth and gender.', 'Contact number and email (if any).', 'Home address.'] },
  { icon: CalendarCheck, t: 'Admission date & representative', d: 'Tell us when the resident plans to move in and who we should contact.', Mock: MockRep,
    pts: ['Expected admission date.', "Representative's name, relationship and I/C no.", 'Contact number (required).', 'Email: invoices and receipts are sent here.'] },
  { icon: ClipboardList, t: 'Confirm and submit', d: 'Tick the box, then submit. You go straight to the document signing section.', Mock: MockConfirm,
    pts: ['Check the information is correct.', 'Tick to confirm.', 'Click Submit Registration.'] },
  { icon: PenLine, t: 'Sign the Resident Admission Form', d: 'Your details are already filled in. Add anything missing, then sign.', Mock: MockAdmission,
    pts: ['Sign inside the box with your finger or mouse (resident or representative).', 'Check the name, I/C no. and date.', 'Click Sign and submit.'] },
  { icon: FileText, t: 'Sign PDPA Consent & Resident Contract', d: 'Read each document, sign at the bottom and submit.', Mock: MockPdpa,
    pts: ['Read the PDPA Notice & Consent.', 'Sign and submit.', 'Next, read the Resident Contract.', 'Sign and submit.'] },
  { icon: ListChecks, t: 'Complete the Admission Checklist', d: 'Tick each item you have prepared for the move-in day.', Mock: MockChecklist,
    pts: ['Tick the documents, medical records and belongings that are ready.', 'Sign as "Checked by" and submit.'] },
  { icon: Download, t: 'Done! Download your copy', d: 'All 4 documents are signed and sent to the care home.', Mock: MockDone,
    pts: ['All documents show a green tick.', 'Click Download Signed Documents (PDF) to keep a copy.'] },
];

const StepCard = ({ s, i }) => (
  <article style={{ animationDelay: `${i * 70}ms` }} data-testid={`guide-step-${i + 1}`}
    className="animate-in fade-in slide-in-from-bottom-4 duration-700 fill-mode-both rounded-3xl bg-white border border-[#E3ECEA] p-6 sm:p-7 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_rgba(11,59,60,0.45)] transition-[transform,box-shadow] duration-300">
    <div className="flex gap-4">
      <div className="flex flex-col items-center gap-3 shrink-0">
        <span className="w-12 h-12 rounded-full bg-[#0B4B4C] text-white grid place-items-center text-xl font-extrabold">{i + 1}</span>
        <span className="w-11 h-11 rounded-full bg-[#E3F4F1] grid place-items-center"><s.icon className="w-5 h-5 text-teal-700" /></span>
      </div>
      <div className="min-w-0">
        <h2 className="text-lg font-bold text-[#0B2E2F] leading-snug">{s.t}</h2>
        <p className="text-sm text-slate-600 mt-1">{s.d}</p>
      </div>
    </div>
    <div className="mt-5 grid md:grid-cols-[1.35fr_1fr] gap-5 items-start">
      <s.Mock />
      <ul className="space-y-2.5">
        {s.pts.map((p) => (
          <li key={p} className="flex gap-2 text-[13px] text-slate-700"><CheckCircle2 className="w-[18px] h-[18px] text-teal-600 shrink-0 mt-px" />{p}</li>
        ))}
      </ul>
    </div>
  </article>
);

const Hero = () => (
  <section className="relative overflow-hidden bg-[#08292A]" data-testid="guide-hero">
    <img src="/og/care-guide-hero.jpg" alt="Caregiver with an elderly resident" className="absolute inset-0 w-full h-full object-cover object-right opacity-90" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#08292A] via-[#08292A]/90 to-transparent" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <div className="flex items-center gap-3">
        <span className="w-12 h-12 rounded-2xl bg-teal-500 grid place-items-center"><HeartHandshake className="w-6 h-6 text-white" /></span>
        <div><p className="text-white font-bold text-xl leading-none">StayMii Care</p><p className="text-teal-200/80 text-sm mt-1">Care Management System</p></div>
      </div>
      <h1 className="mt-10 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] max-w-2xl" data-testid="guide-title">
        How to register<br /><span className="text-[#5EEAD4]">a resident online</span>
      </h1>
      <p className="mt-5 text-base md:text-lg text-slate-200 max-w-xl">A simple guide for families: the registration form, Admission Form, PDPA Consent, Resident Contract and Admission Checklist, all signed online in about 10 minutes.</p>
      <div className="mt-8 inline-flex items-center gap-3 rounded-full bg-white/95 pl-2 pr-6 py-2 shadow-lg" data-testid="guide-url-pill">
        <span className="w-9 h-9 rounded-full bg-teal-600 grid place-items-center"><Link2 className="w-4 h-4 text-white" /></span>
        <span className="font-bold text-[#0B2E2F]">staymii.com/care/register/<span className="text-teal-700">your-care-home</span></span>
      </div>
    </div>
  </section>
);

const FORMS = ['Registration form', 'Resident Admission Form', 'PDPA Notice & Consent', "Resident's Contract", 'Admission Checklist'];

export default function CareGuide() {
  return (
    <div className="min-h-screen bg-[#F3F8F7]" data-testid="care-guide-page">
      <SEO title="How to Register a Resident Online — StayMii Care Guide"
        description="Step-by-step guide for families: fill in the StayMii Care registration form, Resident Admission Form, PDPA consent, Resident Contract and Admission Checklist online."
        canonicalUrl="/staymii-care/guide" ogImage="https://staymii.com/og/care-guide-hero.jpg" />
      <LandingNavBar />
      <Hero />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 -mt-8 relative">
        <div className="rounded-2xl bg-white border border-[#E3ECEA] p-5 flex flex-wrap gap-2.5 items-center shadow-sm" data-testid="guide-forms-list">
          <span className="text-xs font-semibold tracking-wide text-teal-800 mr-2">YOU WILL COMPLETE</span>
          {FORMS.map((f, i) => <span key={f} className="px-3 py-1.5 rounded-full bg-[#E3F4F1] text-[13px] text-[#0B4B4C] font-medium">{i + 1}. {f}</span>)}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-2 gap-6">
        {STEPS.map((s, i) => <StepCard key={s.t} s={s} i={i} />)}
      </section>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="rounded-3xl bg-[#0B4B4C] text-white p-8 sm:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center" data-testid="guide-help">
          <div>
            <p className="text-2xl font-bold">Need help?</p>
            <p className="text-teal-100/90 mt-2 text-sm max-w-2xl">Please contact your care home if you do not have a registration link, or if you have questions about any of the forms. You can stop and continue later using the same signing link (valid for 14 days).</p>
          </div>
          <p className="italic text-[#5EEAD4] text-xl font-semibold">Care management, made simple.</p>
        </div>
      </section>
      <LandingFooter />
    </div>
  );
}
